import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Eye,
  Link2,
  Sparkles,
  Type,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { InvitationPreview } from '../components/InvitationPreview';
import { Skeleton } from '../components/ui/skeleton';
import { SampleInvitation } from '../components/brand/SampleInvitation';
import { HeroShowcase } from '../components/brand/HeroShowcase';
import { Waveform } from '../components/brand/Waveform';
import { GradientSwash } from '../components/brand/GradientSwash';
import { API_URL } from '../utils/api';
import { loadFontStylesheets } from '../utils/fonts';
import { formatPrice, getTemplatePrice } from '../utils/format';
import { CONTACT_PHONE, telHref } from '../config/site';
import { useDocumentTitle } from '../hooks/use-document-title';

const FALLBACK_SIMCHAS = ['חתונה', 'תנאים', 'בר מצוה', 'ברית', 'קידוש', 'שבע ברכות'];

const STEPS = [
  { title: 'Pick a design', text: 'Browse by simcha, or enter a design code from our printed catalog.' },
  { title: 'Add your details', text: 'Type your names and details in Hebrew, Yiddish or English and watch the invitation update.' },
  { title: 'Download & share', text: 'Pay securely, download the print-ready file, and send guests your share link.' },
];

const EDITOR_DEMO_FIELDS = ['שם הבר מצוה', 'שבת פרשת ויצא', '[מקום השמחה]'];

// Phone/SMS ordering is described only in the #phone section near the bottom of the page.
const PHONE_FEATURES = [
  'Choose a design by the 4-digit code in our catalog',
  'Say all the details at once, in Yiddish or English',
  'Hear every name read back to you in Yiddish',
  'Pay securely by phone',
  'Get your preview, catalog and invitation by text message or email',
];

const PHONE_MENU = [
  { key: '1', label: 'Create an invitation', active: true },
  { key: '2', label: 'Get a catalog' },
  { key: '3', label: 'My invitations' },
  { key: '4', label: 'Help' },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6 },
};

const NEW_THIS_SEASON = 4;

// "New this season": the newest designs first, the same order as Newest on the designs page
// (a template without a date goes last; ties keep the catalog order). Several templates share one
// artwork, so each is shown once: by its background code (shared by every template on that artwork,
// even when each has its own thumbnail file) and by its picture.
const newestTemplates = (templates, count = NEW_THIS_SEASON) => {
  const time = (t) => Date.parse(t.created_at) || 0;
  const seen = new Set();
  const picked = [];
  for (const t of [...(templates || [])].sort((a, b) => time(b) - time(a))) {
    if (!t || !t.id) continue;
    const keys = [t.background_code && `code:${t.background_code}`, t.thumbnail_image || t.background_image || t.id].filter(Boolean);
    if (keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    picked.push(t);
    if (picked.length === count) break;
  }
  return picked;
};

const Eyebrow =({ children, className = '' }) => (
  <p className={`text-xs sm:text-[13px] font-bold tracking-[0.16em] uppercase text-[#6E5B1C] ${className}`}>{children}</p>
);

export default function LandingPage() {
  useDocumentTitle(null);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    // Load independently so one failing request doesn't hide the other section.
    Promise.allSettled([axios.get(`${API_URL}/categories`), axios.get(`${API_URL}/templates`)]).then(
      ([catRes, tmplRes]) => {
        if (cancelled) return;
        if (catRes.status === 'fulfilled' && Array.isArray(catRes.value.data)) setCategories(catRes.value.data);
        if (tmplRes.status === 'fulfilled' && Array.isArray(tmplRes.value.data)) setTemplates(tmplRes.value.data);
        setStatus('ready');
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // the template previews (hero and designs) use the uploaded fonts too, as on the templates page
  useEffect(() => {
    axios
      .get(`${API_URL}/fonts`)
      .then((response) => loadFontStylesheets(response.data))
      .catch(() => {
        // Previews fall back to default fonts.
      });
  }, []);

  // partner templates carry their own fonts (left out of the site-wide /fonts list)
  useEffect(() => {
    templates.forEach((t) => loadFontStylesheets(t.fonts));
  }, [templates]);

  const occasionCategories = categories.filter((cat) => !cat.parent_id);
  const featuredTemplates = useMemo(() => newestTemplates(templates), [templates]);

  return (
    <div className="min-h-screen bg-paper overflow-x-hidden">
      <Navbar />

      <main id="main-content">
        {/* ── Hero ───────────────────────────────────────────── */}
        <section className="relative pt-28 sm:pt-36 px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl mx-auto flex flex-col items-center text-center gap-6"
          >
            <h1 className="font-serif font-semibold text-ink text-[44px] leading-[1.02] sm:text-7xl lg:text-[104px] lg:leading-[0.98] tracking-[-0.025em]">
              Invitations as{' '}
              <span className="relative inline-block italic font-medium">
                beautiful
                <GradientSwash className="-bottom-2 sm:-bottom-3 h-3 sm:h-5" />
              </span>
              <br className="hidden sm:block" /> as your simcha.
            </h1>

            <p className="text-base sm:text-xl leading-relaxed text-ink/70 max-w-2xl">
              Pick a design, type your names and details in the live editor, and download a print-ready
              invitation in minutes. See it before you pay.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Link
                to="/templates"
                data-testid="hero-browse-btn"
                className="h-14 px-8 rounded-full bg-ink text-paper text-base sm:text-[17px] font-bold inline-flex items-center justify-center gap-2 shadow-[0_16px_30px_-14px_rgba(26,26,26,0.6)] hover:bg-ink/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
              >
                Browse designs <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
              <a
                href="#how"
                data-testid="hero-start-btn"
                className="h-14 px-7 rounded-full gradient-border-strong gb-paper text-base sm:text-[17px] font-bold inline-flex items-center justify-center gap-2 text-ink hover:bg-cream transition-colors"
              >
                How it works
              </a>
            </div>

            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-ink/70">
              {['Free preview before you pay', 'Hebrew & Yiddish fonts', 'Online share link included'].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-ink" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Stage: fanned real templates + floating feature cards */}
          <div className="relative max-w-6xl mx-auto h-[360px] sm:h-[560px] lg:h-[640px] mt-6 sm:mt-10" aria-hidden="true">
            <div className="absolute left-[12%] top-10 w-[300px] h-[300px] sm:w-[520px] sm:h-[520px] rounded-full glow-pink" />
            <div className="absolute right-[10%] top-0 w-[320px] h-[320px] sm:w-[560px] sm:h-[560px] rounded-full glow-blue" />

            <HeroShowcase templates={templates} loading={status === 'loading'} />

            <div className="hidden sm:flex animate-float absolute -left-2 lg:left-0 top-[40px] z-10 w-[250px] flex-col gap-2.5 p-4 rounded-2xl bg-white/95 border border-[#EAE4D8] shadow-[0_24px_40px_-22px_rgba(26,26,26,0.4)] text-left">
              <div className="flex items-center gap-2.5 text-[13px] font-bold">
                <span className="h-7 w-7 rounded-full bg-ink flex items-center justify-center">
                  <Type className="h-3.5 w-3.5 text-white" />
                </span>
                Live editor
              </div>
              <div dir="rtl" className="h-9 rounded-lg gradient-border-strong font-hebrew text-[17px] flex items-center px-3">שם הבר מצוה|</div>
              <p className="text-[13px] leading-snug text-ink/70">Type in Hebrew or Yiddish and watch the invitation update.</p>
            </div>

            <div className="hidden sm:flex animate-float-slow absolute right-[2%] lg:right-[6%] top-[90px] z-10 w-[240px] flex-col gap-2.5 p-4 rounded-2xl bg-white/95 border border-[#EAE4D8] shadow-[0_24px_40px_-22px_rgba(26,26,26,0.4)] text-left">
              <p className="text-[13px] font-bold text-ink/70">Print-ready download</p>
              <p className="font-serif text-2xl font-semibold leading-none">PDF &amp; PNG</p>
              <div className="h-2 rounded-full bg-[#EEEBE4] overflow-hidden">
                <div className="h-full w-2/3 rounded-full bg-logo-gradient" />
              </div>
            </div>

            <div className="animate-float-slower absolute right-[4%] sm:right-[12%] bottom-2 sm:bottom-auto sm:top-[430px] z-10 flex items-center gap-3 px-4 py-3 rounded-2xl bg-ink text-paper text-[13px] sm:text-sm font-semibold shadow-[0_24px_40px_-20px_rgba(26,26,26,0.6)]">
              <span className="h-7 w-7 rounded-full gradient-border-strong gb-ink flex items-center justify-center">
                <Check className="h-3.5 w-3.5 text-white" />
              </span>
              Free preview, pay when it's right
            </div>
          </div>
        </section>

        {/* ── Designs for every simcha ──────────────────────── */}
        <section className="px-4 sm:px-6 pt-10 pb-16 sm:pb-24" aria-labelledby="simchas-heading">
          <p id="simchas-heading" className="text-center text-xs sm:text-[13px] font-bold tracking-[0.16em] uppercase text-ink/60 mb-6">
            Designs for every simcha
          </p>
          {status === 'loading' ? (
            <div className="flex justify-center gap-6">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-28 rounded-full" />
              ))}
            </div>
          ) : occasionCategories.length > 0 ? (
            <ul className="flex flex-wrap justify-center items-center gap-x-4 gap-y-3 sm:gap-x-8 max-w-6xl mx-auto">
              {occasionCategories.map((category, i) => (
                <li key={category.id} className="flex items-center gap-4 sm:gap-8">
                  <Link
                    to={`/templates?category=${category.id}`}
                    data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="font-serif text-2xl sm:text-4xl text-ink hover:text-[#6E5B1C] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  >
                    {category.name}
                  </Link>
                  {i < occasionCategories.length - 1 && (
                    <span className="h-2 w-2 rotate-45 bg-logo-gradient" aria-hidden="true" />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p dir="rtl" className="flex flex-wrap justify-center gap-x-8 gap-y-2 font-hebrew text-3xl sm:text-4xl text-ink">
              {FALLBACK_SIMCHAS.map((w) => (
                <span key={w}>{w}</span>
              ))}
            </p>
          )}
        </section>

        {/* ── Features (bento) ──────────────────────────────── */}
        <section id="features" className="px-4 sm:px-6 pb-20 sm:pb-28" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto">
            <motion.div {...fadeUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-10">
              <h2 id="features-heading" className="font-serif font-semibold text-4xl sm:text-6xl leading-[1.05] tracking-[-0.015em] max-w-3xl">
                Everything for your invitation, <span className="italic font-medium">in one place.</span>
              </h2>
              <p className="text-base sm:text-[17px] leading-relaxed text-ink/70 max-w-sm">
                From choosing a design to sharing it with guests, all online, in a few minutes.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
              <motion.div {...fadeUp} className="lg:col-span-7 lg:row-span-2 relative overflow-hidden rounded-[28px] bg-ink text-paper p-7 sm:p-11 flex flex-col gap-5 min-h-[460px] lg:min-h-[600px]">
                <div className="absolute -right-32 -bottom-40 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(236,0,140,0.28),rgba(236,0,140,0)_60%)]" aria-hidden="true" />
                <div className="absolute right-16 -bottom-56 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(12,191,242,0.26),rgba(12,191,242,0)_60%)]" aria-hidden="true" />
                <Eyebrow className="!text-[#D9C27A] relative">Live editor</Eyebrow>
                <h3 className="relative font-serif text-3xl sm:text-[44px] leading-[1.08] font-semibold max-w-lg">Type your details, see it instantly.</h3>
                <p className="relative text-base sm:text-[17px] leading-relaxed text-[#CFCAC0] max-w-md">
                  Fill in the names, date and place in Hebrew, Yiddish or English. The invitation updates as you
                  type, and you can drag any line, change fonts and colours.
                </p>
                <div className="relative mt-auto flex items-end gap-5 sm:gap-8 max-w-lg" aria-hidden="true">
                  <div className="flex-1 flex flex-col gap-2.5">
                    {EDITOR_DEMO_FIELDS.map((value, i) => (
                      <div
                        key={value}
                        dir="rtl"
                        className={`h-11 sm:h-12 rounded-xl font-hebrew text-lg sm:text-xl flex items-center px-4 ${i === 0 ? 'gradient-border-strong [--gb-bg:#232220]' : 'bg-[#2B2A27]'}`}
                      >
                        {value}
                        {i === 0 ? '|' : ''}
                      </div>
                    ))}
                  </div>
                  <div className="w-[110px] sm:w-[160px] shrink-0">
                    <SampleInvitation tone="midnight" kicker="בר מצוה" names={['שם הבר מצוה']} />
                  </div>
                </div>
              </motion.div>

              <motion.div {...fadeUp} className="lg:col-span-5 rounded-[28px] gradient-border p-7 sm:p-8 flex items-center gap-6">
                <div className="flex-1 flex flex-col gap-2.5">
                  <h3 className="font-serif text-2xl sm:text-[28px] font-semibold">Have a printed catalog?</h3>
                  <p className="text-[15px] leading-relaxed text-ink/70">Enter the design code from the catalog to find that design straight away.</p>
                </div>
                <div className="w-[120px] sm:w-[150px] shrink-0 flex flex-col gap-2" aria-hidden="true">
                  <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-ink/60">Design code</p>
                  <div className="h-10 rounded-lg gradient-border-strong font-serif text-xl font-semibold flex items-center px-3">1042|</div>
                </div>
              </motion.div>

              <motion.div {...fadeUp} className="lg:col-span-5 rounded-[28px] gradient-border p-7 sm:p-8 flex flex-col gap-4">
                <h3 className="font-serif text-2xl sm:text-[28px] font-semibold">One link to share</h3>
                <p className="text-[15px] leading-relaxed text-ink/70">Send guests a single link. The invitation opens beautifully on any phone, no app needed.</p>
                <div className="flex items-center gap-3 mt-1 px-4 py-3 rounded-full bg-[#F4F1EA] text-sm font-semibold" aria-hidden="true">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-ink flex items-center justify-center">
                    <Link2 className="h-3.5 w-3.5 text-white" />
                  </span>
                  <span className="truncate text-ink/70">azamein.com/share/your-simcha</span>
                  <span className="ml-auto shrink-0 px-3 py-1 rounded-full bg-logo-gradient text-white text-xs font-bold">Copy</span>
                </div>
              </motion.div>

              <motion.div {...fadeUp} className="lg:col-span-4 rounded-[28px] bg-cream p-7 sm:p-8 flex flex-col gap-3.5">
                <div className="flex gap-2.5" aria-hidden="true">
                  <span className="w-12 h-16 rounded-lg bg-white border border-border flex items-end justify-center pb-2 text-[11px] font-extrabold text-[#B0003A]">PDF</span>
                  <span className="w-12 h-16 rounded-lg bg-white border border-border flex items-end justify-center pb-2 text-[11px] font-extrabold text-[#1D5E8C]">JPG</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-[26px] font-semibold">Catalogs, sent to you</h3>
                <p className="text-[15px] leading-relaxed text-ink/70">Get any category's catalog by email, as a PDF or picture, to browse at home or print.</p>
              </motion.div>

              <motion.div {...fadeUp} className="lg:col-span-4 rounded-[28px] bg-cream p-7 sm:p-8 flex flex-col gap-3.5">
                <p dir="rtl" className="font-hebrew text-5xl sm:text-6xl font-black leading-none tracking-wide text-logo-gradient" aria-hidden="true">אבג</p>
                <h3 className="font-serif text-2xl sm:text-[26px] font-semibold">Real Hebrew lettering</h3>
                <p className="text-[15px] leading-relaxed text-ink/70">Fonts chosen for Hebrew and Yiddish invitations, set right to left properly.</p>
              </motion.div>

              <motion.div {...fadeUp} className="lg:col-span-4 rounded-[28px] bg-cream p-7 sm:p-8 flex flex-col gap-3.5">
                <span className="w-16 h-16 rounded-[18px] gradient-border-strong flex items-center justify-center" aria-hidden="true">
                  <Eye className="h-7 w-7" />
                </span>
                <h3 className="font-serif text-2xl sm:text-[26px] font-semibold">Preview first, pay after</h3>
                <p className="text-[15px] leading-relaxed text-ink/70">See a watermarked preview of your invitation. Pay only when it's exactly right.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Featured designs (real templates) ─────────────── */}
        <section className="bg-cream px-4 sm:px-6 py-20 sm:py-24" aria-labelledby="featured-heading">
          <div className="max-w-7xl mx-auto">
            <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
              <div className="flex flex-col gap-3">
                <Eyebrow>Featured designs</Eyebrow>
                <h2 id="featured-heading" className="font-serif font-semibold text-4xl sm:text-[56px] tracking-[-0.015em] leading-tight">New this season</h2>
              </div>
              <Link
                to="/templates?sort=newest"
                className="self-start sm:self-auto h-12 px-6 rounded-full gradient-border-strong gb-cream font-bold inline-flex items-center gap-2 hover:bg-white"
              >
                All designs <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </motion.div>

            {status === 'loading' ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="aspect-[5/7] rounded-[22px]" />
                ))}
              </div>
            ) : featuredTemplates.length === 0 ? (
              <div className="rounded-[22px] gradient-border gb-paper p-10 text-center">
                <p className="text-ink/70 mb-4">New designs are on their way.</p>
                <Link to="/templates" className="font-bold underline underline-offset-4">Browse all designs</Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {featuredTemplates.map((template, index) => (
                  <motion.div key={template.id} {...fadeUp} transition={{ duration: 0.6, delay: index * 0.08 }}>
                    <Link
                      to={`/editor/${template.id}`}
                      data-testid={`template-${template.id}`}
                      className="group flex flex-col gap-3 sm:gap-4 rounded-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
                    >
                      <div className={`rounded-[22px] p-4 sm:p-7 ${index === 0 ? 'gradient-border-strong' : 'bg-white border-[1.5px] border-[#E6E1D6]'}`}>
                        <div className="rounded overflow-hidden shadow-[0_22px_36px_-18px_rgba(26,26,26,0.5)] transition-transform duration-500 group-hover:-translate-y-1.5">
                          <InvitationPreview
                            backgroundImage={template.thumbnail_image || template.background_image}
                            fields={template.text_fields}
                            perspective={template.perspective}
                            getText={(field) => field.default_text || field.text || ''}
                            aspectRatio="5 / 7"
                            textShadow="0 1px 3px rgba(0,0,0,0.3), 0 1px 1px rgba(255,255,255,0.8)"
                            alt={`${template.name} invitation template`}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 px-1">
                        <div className="min-w-0">
                          <p className="font-bold text-[15px] sm:text-[17px] truncate">{template.name}</p>
                          <p className="text-xs sm:text-[13px] text-ink/60">
                            {template.template_code ? `Code ${template.template_code} · ` : ''}
                            {formatPrice(getTemplatePrice(template))}
                          </p>
                        </div>
                        <span className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-ink text-white flex items-center justify-center transition-transform group-hover:rotate-45" aria-hidden="true">
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── How it works ──────────────────────────────────── */}
        <section id="how" className="px-4 sm:px-6 py-20 sm:py-28" aria-labelledby="how-heading">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeUp} className="text-center flex flex-col items-center gap-3 mb-14">
              <Eyebrow>How it works</Eyebrow>
              <h2 id="how-heading" className="font-serif font-semibold text-4xl sm:text-[56px] tracking-[-0.015em]">Ready in three steps</h2>
            </motion.div>
            <ol className="relative grid gap-12 md:grid-cols-3 md:gap-10">
              <span className="hidden md:block absolute left-[16.6%] right-[16.6%] top-9 h-0.5 bg-logo-gradient opacity-60" aria-hidden="true" />
              {STEPS.map((step, i) => (
                <motion.li key={step.title} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="relative flex flex-col items-center text-center gap-4">
                  <span className="h-[72px] w-[72px] rounded-full gradient-border-strong gb-paper border-2 flex items-center justify-center font-serif italic text-3xl font-semibold">
                    {i + 1}
                  </span>
                  <h3 className="text-xl font-extrabold">{step.title}</h3>
                  <p className="text-base leading-relaxed text-ink/70 max-w-xs">{step.text}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── Closing call to action ───────────────────────── */}
        <section className="px-4 sm:px-6 pb-20 sm:pb-28">
          <motion.div {...fadeUp} className="max-w-7xl mx-auto rounded-[28px] sm:rounded-[36px] p-[2px] bg-logo-gradient">
            <div className="rounded-[26px] sm:rounded-[34px] bg-[#FBF7EE] px-6 py-14 sm:p-20 flex flex-col items-center text-center gap-5">
              <p dir="rtl" lang="he" className="font-hebrew text-2xl text-[#8A7322]">מזל טוב!</p>
              <h2 className="font-serif font-semibold text-4xl sm:text-[64px] leading-[1.05] tracking-[-0.02em] max-w-3xl">
                Let's make your invitation <span className="italic font-medium">today.</span>
              </h2>
              <p className="text-base sm:text-lg text-ink/70">Pick a design and have it ready to print in minutes.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-2 w-full sm:w-auto">
                <Link
                  to="/templates"
                  data-testid="cta-start-btn"
                  className="h-14 px-8 rounded-full bg-ink text-paper font-bold inline-flex items-center justify-center gap-2 hover:bg-ink/90"
                >
                  <Sparkles className="h-5 w-5" aria-hidden="true" /> Browse designs
                </Link>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ── Phone & text message ordering (all phone/SMS info lives here) ── */}
        <section id="phone" className="px-4 sm:px-6 pb-20 sm:pb-28 scroll-mt-24" aria-labelledby="phone-heading">
          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[28px] sm:rounded-[36px] bg-ink text-paper p-8 sm:p-14 lg:p-20 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="absolute -left-40 -top-52 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(236,0,140,0.22),rgba(236,0,140,0)_62%)]" aria-hidden="true" />
            <div className="relative flex flex-col gap-6">
              <Eyebrow className="!text-[#D9C27A]">No computer? Order by phone</Eyebrow>
              <h2 id="phone-heading" className="font-serif font-semibold text-4xl sm:text-[56px] leading-[1.05] tracking-[-0.015em]">
                One phone call.
                <br />
                <span className="italic font-medium">Your invitation, done.</span>
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-[#CFCAC0] max-w-md">
                For family members without a computer: call our phone line and order the whole invitation by
                voice, in Yiddish or English.
              </p>
              <ul className="flex flex-col gap-3 max-w-md">
                {PHONE_FEATURES.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] sm:text-base leading-snug">
                    <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full gradient-border-strong [--gb-bg:#1A1A1A] flex items-center justify-center" aria-hidden="true">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              {CONTACT_PHONE ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#9C978C]">Call us</p>
                  <a href={telHref(CONTACT_PHONE)} className="font-serif text-4xl sm:text-5xl font-semibold text-logo-gradient-soft w-fit">
                    {CONTACT_PHONE}
                  </a>
                </div>
              ) : null}
            </div>

            <div className="relative flex justify-center" aria-hidden="true">
              <div className="w-[270px] sm:w-[300px] h-[540px] sm:h-[600px] rounded-[48px] p-[3px] bg-[linear-gradient(150deg,#EC008C,#0CBFF2)] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
                <div className="h-full rounded-[45px] bg-[#111110] px-6 pt-14 pb-8 flex flex-col items-center gap-3">
                  <p className="text-[13px] font-semibold text-[#9C978C]">Calling</p>
                  <p className="font-serif text-3xl font-semibold">Azamein</p>
                  <Waveform bars={14} scale={1.3} className="h-9 my-2" />
                  <ul className="w-full flex flex-col gap-2">
                    {PHONE_MENU.map((m) => (
                      <li
                        key={m.key}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl ${m.active ? 'gradient-border-strong [--gb-bg:#232220]' : 'bg-[#1D1C1A] border-[1.5px] border-[#1D1C1A]'}`}
                      >
                        <span className="h-8 w-8 rounded-full border border-[#4A4640] flex items-center justify-center text-sm font-extrabold">{m.key}</span>
                        <span className="text-sm font-semibold">{m.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
